import { useState } from 'react';
import { Bell, Menu } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/cliente.js';
import { Pill } from '../componentes/ui/Pill.js';
import { BuscaGlobal } from './BuscaGlobal.js';
import { TarefasIndicador } from './TarefasIndicador.js';

interface TopbarProps {
  titulo: string;
  subtitulo: string;
  aoAbrirMenu: () => void;
  acao?: React.ReactNode;
}

interface SaudePdns {
  no: string;
  ok: boolean;
  versao?: string;
}

interface Alerta {
  chave: string;
  tipo: string;
  titulo: string;
  quando: string;
  naoLida: boolean;
}

export function Topbar({ titulo, subtitulo, aoAbrirMenu, acao }: TopbarProps) {
  const { data: nos } = useQuery({
    queryKey: ['pdns', 'saude'],
    queryFn: () => api.get<SaudePdns[]>('/infra/powerdns/saude'),
    refetchInterval: 30_000,
    // Falha aqui não deve encher a tela de erro: é um indicador, não a função
    // principal da página.
    retry: false,
  });

  const online = nos?.filter((n) => n.ok).length ?? 0;
  const total = nos?.length ?? 0;
  const tudoOk = total > 0 && online === total;

  const qc = useQueryClient();
  const [sinoAberto, setSinoAberto] = useState(false);

  const { data: notif } = useQuery({
    queryKey: ['notificacoes'],
    queryFn: () => api.get<{ itens: Alerta[]; naoLidas: number }>('/notificacoes'),
    refetchInterval: 60_000,
    retry: false,
  });

  const marcarLidas = useMutation({
    mutationFn: () => api.post('/notificacoes/marcar-lidas'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notificacoes'] }),
  });

  const abrirSino = () => {
    const abrindo = !sinoAberto;
    setSinoAberto(abrindo);
    // Ao abrir, zera o contador: o operador viu os alertas.
    if (abrindo && (notif?.naoLidas ?? 0) > 0) marcarLidas.mutate();
  };

  const naoLidas = notif?.naoLidas ?? 0;

  return (
    <header className="sticky top-0 z-20 flex h-topbar shrink-0 items-center gap-4 border-b border-borda bg-superficie px-4 lg:px-6">
      <button
        onClick={aoAbrirMenu}
        className="grid size-9 shrink-0 place-items-center rounded-chip text-texto-suave hover:bg-borda-suave lg:hidden"
        aria-label="Abrir menu"
      >
        <Menu size={20} />
      </button>

      <div className="min-w-0 flex-1 lg:flex-none">
        <h1 className="truncate text-[18px] font-extrabold leading-tight">{titulo}</h1>
        <p className="truncate text-[12.5px] text-texto-suave">{subtitulo}</p>
      </div>

      <BuscaGlobal />

      <div className="flex shrink-0 items-center gap-3">
        {total > 0 && (
          <Pill tom={tudoOk ? 'ok' : 'aviso'} className="hidden sm:inline-flex">
            <span
              className={`mr-1.5 size-1.5 rounded-full ${tudoOk ? 'bg-sucesso' : 'bg-aviso'}`}
              aria-hidden="true"
            />
            PowerDNS {online}/{total}
          </Pill>
        )}

        <TarefasIndicador />

        <div className="relative">
          <button
            onClick={abrirSino}
            className="relative grid size-9 place-items-center rounded-chip text-texto-suave hover:bg-borda-suave"
            aria-label={`Notificações${naoLidas > 0 ? ` (${naoLidas} não lidas)` : ''}`}
          >
            <Bell size={19} />
            {naoLidas > 0 && (
              <span className="absolute -right-0.5 -top-0.5 grid min-w-4 place-items-center rounded-pill bg-erro px-1 text-[10px] font-bold leading-4 text-white">
                {naoLidas > 9 ? '9+' : naoLidas}
              </span>
            )}
          </button>

          {sinoAberto && (
            <>
              {/* Fundo invisível que fecha ao clicar fora. */}
              <button
                className="fixed inset-0 z-30 cursor-default"
                aria-hidden="true"
                tabIndex={-1}
                onClick={() => setSinoAberto(false)}
              />
              <div className="absolute right-0 top-11 z-40 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-card border border-borda bg-superficie shadow-lg">
                <div className="border-b border-borda-suave px-4 py-2.5">
                  <p className="text-[13px] font-bold">Notificações</p>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {(notif?.itens.length ?? 0) === 0 ? (
                    <p className="px-4 py-6 text-center text-[12.5px] text-texto-suave">
                      Nenhum alerta recente.
                    </p>
                  ) : (
                    notif!.itens.map((a) => (
                      <div
                        key={a.chave}
                        className="flex items-start gap-2.5 border-b border-borda-suave px-4 py-2.5 last:border-0"
                      >
                        <span
                          className={`mt-1.5 size-1.5 shrink-0 rounded-full ${
                            a.tipo === 'backup-problema' || a.chave.endsWith(':falha')
                              ? 'bg-erro'
                              : a.tipo === 'solicitacao-pendente'
                                ? 'bg-aviso'
                                : 'bg-sucesso'
                          }`}
                        />
                        <div className="min-w-0">
                          <p className="text-[12.5px] font-semibold leading-snug">{a.titulo}</p>
                          <p className="text-[11.5px] text-texto-suave">{tempoRelativo(a.quando)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {acao}
      </div>
    </header>
  );
}

/** "há 3 min", "há 2 h", "há 4 d" — o suficiente para um sino. */
function tempoRelativo(iso: string): string {
  const seg = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seg < 60) return 'agora';
  const min = Math.floor(seg / 60);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  return `há ${d} d`;
}
